import type {
  ContactField,
  FicheClient360State,
  FicheClient360StoreDeps,
  OTPSendResult,
  OTPVerifyResult,
} from "../types";
import { computeOtpDestination, isException } from "../helpers";

export function createOtpSlice(
  deps: FicheClient360StoreDeps,
  set: (
    partial:
      | Partial<FicheClient360State>
      | ((state: FicheClient360State) => Partial<FicheClient360State>),
  ) => void,
  get: () => FicheClient360State,
): Partial<FicheClient360State> {
  return {
    sendUpdateOTP: async (
      field: ContactField,
      destination: string,
    ): Promise<OTPSendResult> => {
      const challengeKey =
        `update${field.charAt(0).toUpperCase() + field.slice(1)}ChallengeId` as keyof FicheClient360State;
      const verifiedKey =
        `update${field.charAt(0).toUpperCase() + field.slice(1)}Verified` as keyof FicheClient360State;

      try {
        const state = get();
        const businessKey = state.kycData?.businessKey;
        if (!businessKey) {
          return {
            success: false,
            errorMessage: "NO_BUSINESS_KEY",
          };
        }

        const { channel, localDestination, countryCode } =
          computeOtpDestination(field, state.kycData, destination);

        const result = await deps.contactVerificationService.initiate({
          businessKey,
          channel,
          destination: localDestination,
          countryCode,
        });

        if (result.success && result.data?.data?.transactionId) {
          set({
            [challengeKey]: result.data.data.transactionId,
            [verifiedKey]: false,
          } as Partial<FicheClient360State>);
          return {
            success: true,
            challengeId: result.data.data.transactionId,
          };
        }
        return {
          success: false,
          errorMessage: result.exception?.message ?? undefined,
        };
      } catch (error) {
        const message =
          isException(error) && typeof error.message === "string"
            ? (error.message as string)
            : undefined;
        return { success: false, errorMessage: message };
      }
    },

    verifyUpdateOTP: async (
      field: ContactField,
      code: string,
      challengeId: string,
    ): Promise<OTPVerifyResult> => {
      const verifiedKey =
        `update${field.charAt(0).toUpperCase() + field.slice(1)}Verified` as keyof FicheClient360State;
      const challengeKey =
        `update${field.charAt(0).toUpperCase() + field.slice(1)}ChallengeId` as keyof FicheClient360State;

      try {
        const result = await deps.contactVerificationService.confirm({
          transactionId: challengeId,
          code,
        });

        if (result.success) {
          set((state: FicheClient360State) => {
            const next: Partial<FicheClient360State> = {
              [verifiedKey]: true,
              [challengeKey]: null,
            };

            if (state.kycData) {
              const kycData = { ...state.kycData };
              if (field === "phone1") kycData.phone1Verified = true;
              if (field === "phone2") kycData.phone2Verified = true;
              if (field === "phone3") kycData.phone3Verified = true;
              if (field === "phone4") kycData.phone4Verified = true;
              if (field === "email1") kycData.email1Verified = true;
              if (field === "email2") kycData.email2Verified = true;
              next.kycData = kycData;
            }

            return next;
          });
          return { success: true };
        }

        const errorMessage =
          (result.exception?.message as string | undefined) ?? undefined;

        return { success: false, errorMessage };
      } catch (error) {
        const message =
          isException(error) && typeof error.message === "string"
            ? (error.message as string)
            : undefined;
        return { success: false, errorMessage: message };
      }
    },

    resetUpdateOTPState: () => {
      set({
        updatePhone1Verified: false,
        updatePhone2Verified: false,
        updatePhone3Verified: false,
        updatePhone4Verified: false,
        updateEmail1Verified: false,
        updateEmail2Verified: false,
        updatePhone1ChallengeId: null,
        updatePhone2ChallengeId: null,
        updatePhone3ChallengeId: null,
        updatePhone4ChallengeId: null,
        updateEmail1ChallengeId: null,
        updateEmail2ChallengeId: null,
      });
    },

    setUpdateFieldVerified: (field: ContactField, verified: boolean) => {
      const verifiedKey =
        `update${field.charAt(0).toUpperCase() + field.slice(1)}Verified` as keyof FicheClient360State;
      set({ [verifiedKey]: verified } as Partial<FicheClient360State>);
    },
  };
}
